import { getAccountingApiRoot } from './accounting';
import { getAuthHeaders } from './auth';

export type EmployeeAdvancePaymentType = 'advance' | 'on_account' | 'other';

export interface CompanyBankAccountOption {
  id: number;
  name: string;
  bank_name?: string | null;
  account_number?: string | null;
  account_type?: string | null;
  is_default_destination?: boolean;
}

export interface EmployeeAdvanceBankAccount {
  id: number;
  employee_id?: number;
  bank_id?: number | null;
  account_holder_name?: string | null;
  account_number?: string | null;
  iban?: string | null;
  card_number?: string | null;
  notes?: string | null;
  is_default?: boolean;
  is_active?: boolean;
  bank?: { id: number; name: string } | null;
}

export interface EmployeeAdvance {
  id: number;
  employee_id: number;
  project_id: number;
  amount: number;
  payment_type?: EmployeeAdvancePaymentType | null;
  expense_type_id?: number | null;
  expense_type_title?: string | null;
  payment_status?: string | null;
  registered_at: string;
  payroll_month?: string | null;
  source_bank_account_id?: number | null;
  destination_employee_bank_account_id?: number | null;
  bank_movement_id?: number | null;
  bank_reference?: string | null;
  bsi_statement_registration_id?: number | null;
  attachment_path?: string | null;
  attachment_name?: string | null;
  attachment_url?: string | null;
  notes?: string | null;
  project?: { id: number; name: string } | null;
  source_bank_account?: CompanyBankAccountOption | null;
  destination_bank_account?: (EmployeeAdvanceBankAccount & {
    bank?: { id: number; name: string } | null;
  }) | null;
  created_at?: string;
}

export interface AdvanceEligibility {
  earned_salary: number;
  attendance_earned?: number;
  resource_earned?: number;
  previous_advances: number;
  remaining_eligible: number;
  worked_days: number;
  present_days: number;
  half_days: number;
  absent_days: number;
  daily_salary: number;
  deduction_amount: number;
  salary_type?: string | null;
  has_assignment: boolean;
  resource_count?: number;
  payroll_month: string;
}

export interface AdvanceMeta {
  projects: {
    id: number;
    name: string;
    client_name?: string | null;
    salary_type?: string;
    role?: string | null;
    is_contractor?: boolean;
  }[];
  expense_types?: {
    id: number;
    title: string;
    note?: string | null;
    is_active?: boolean;
    caps_to_earned?: boolean;
  }[];
  employee_bank_accounts: EmployeeAdvanceBankAccount[];
  company_bank_accounts: CompanyBankAccountOption[];
}

export interface AdvanceTransferHint {
  advance_id: number;
  amount: number;
  payment_type: EmployeeAdvancePaymentType;
  description: string;
  source_bank_account_id: number;
  destination_employee_bank_account_id?: number | null;
  destination_name?: string | null;
  destination_iban?: string | null;
  destination_card?: string | null;
  destination_account?: string | null;
  destination_bank_name?: string | null;
  suggested_bsi_mode?: 'local' | 'interbank';
  /** مقدار آماده برای فیلد مقصد در اینترنت‌بانک: شماره حساب (داخل بانک) یا شبا (بین‌بانکی) */
  destination_bsi_account?: string | null;
}

export interface AdvanceStoreResponse {
  success: boolean;
  message?: string;
  data?: {
    advance: EmployeeAdvance;
    eligibility?: AdvanceEligibility | null;
    payment?: {
      bank_movement_id?: number | null;
      payment_status?: string;
      transfer_hint?: AdvanceTransferHint | null;
    } | null;
  };
}

async function uploadAdvance(
  method: 'POST' | 'PUT',
  path: string,
  formData: FormData,
): Promise<AdvanceStoreResponse> {
  const url = `${getAccountingApiRoot()}${path}`;
  const r = await fetch(url, {
    method,
    headers: getAuthHeaders(),
    credentials: 'same-origin',
    body: formData,
  });
  const text = await r.text();
  if (!r.ok) {
    let msg = `خطا: ${r.status}`;
    try {
      if (text) {
        const j = JSON.parse(text) as { message?: string; error?: string };
        msg = j.message ?? j.error ?? msg;
      }
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return text ? (JSON.parse(text) as AdvanceStoreResponse) : { success: true };
}

export const advancesApi = {
  list(employeeId: number) {
    return fetch(`${getAccountingApiRoot()}/employees/${employeeId}/advances`, {
      headers: getAuthHeaders(),
    }).then(async (r) => {
      const text = await r.text();
      if (!r.ok) {
        let msg = `خطا: ${r.status}`;
        try {
          if (text) {
            const j = JSON.parse(text) as { message?: string };
            msg = j.message ?? msg;
          }
        } catch {
          /* ignore */
        }
        throw new Error(msg);
      }
      return text
        ? (JSON.parse(text) as { success: boolean; data: { advances: EmployeeAdvance[] } })
        : { success: true, data: { advances: [] } };
    });
  },

  meta(employeeId: number) {
    return fetch(`${getAccountingApiRoot()}/employees/${employeeId}/advances/meta`, {
      headers: getAuthHeaders(),
    }).then(async (r) => {
      const text = await r.text();
      if (!r.ok) throw new Error(`خطا: ${r.status}`);
      return text
        ? (JSON.parse(text) as { success: boolean; data: AdvanceMeta })
        : { success: true, data: { projects: [], employee_bank_accounts: [], company_bank_accounts: [] } };
    });
  },

  eligibility(
    employeeId: number,
    params: { project_id: number; month: string; exclude_advance_id?: number },
  ) {
    const q = new URLSearchParams();
    q.set('project_id', String(params.project_id));
    q.set('month', params.month);
    if (params.exclude_advance_id != null) {
      q.set('exclude_advance_id', String(params.exclude_advance_id));
    }
    return fetch(`${getAccountingApiRoot()}/employees/${employeeId}/advances/eligibility?${q}`, {
      headers: getAuthHeaders(),
    }).then(async (r) => {
      const text = await r.text();
      if (!r.ok) {
        let msg = `خطا: ${r.status}`;
        try {
          if (text) {
            const j = JSON.parse(text) as { message?: string };
            msg = j.message ?? msg;
          }
        } catch {
          /* ignore */
        }
        throw new Error(msg);
      }
      return text
        ? (JSON.parse(text) as { success: boolean; data: AdvanceEligibility })
        : { success: true, data: {} as AdvanceEligibility };
    });
  },

  store(employeeId: number, formData: FormData) {
    return uploadAdvance('POST', `/employees/${employeeId}/advances`, formData);
  },

  update(employeeId: number, advanceId: number, formData: FormData) {
    formData.append('_method', 'PUT');
    return uploadAdvance('POST', `/employees/${employeeId}/advances/${advanceId}`, formData);
  },

  updateExpenseType(employeeId: number, advanceId: number, expenseTypeId: number | null) {
    return fetch(`${getAccountingApiRoot()}/employees/${employeeId}/advances/${advanceId}/expense-type`, {
      method: 'PATCH',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ expense_type_id: expenseTypeId }),
    }).then(async (r) => {
      const json = (await r.json().catch(() => ({}))) as {
        success?: boolean;
        message?: string;
        data?: { advance?: EmployeeAdvance };
      };
      if (!r.ok || json.success === false) {
        throw new Error(json.message || `خطا: ${r.status}`);
      }
      return json;
    });
  },

  destroy(employeeId: number, advanceId: number) {
    return fetch(`${getAccountingApiRoot()}/employees/${employeeId}/advances/${advanceId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    }).then(async (r) => {
      if (!r.ok) throw new Error(`خطا: ${r.status}`);
      const text = await r.text();
      return text ? (JSON.parse(text) as { success: boolean }) : { success: true };
    });
  },
};

export const BSI_TRANSFER_PREFILL_KEY = 'bsi_transfer_prefill';

export function saveBsiTransferPrefill(hint: AdvanceTransferHint): void {
  try {
    sessionStorage.setItem(BSI_TRANSFER_PREFILL_KEY, JSON.stringify(hint));
  } catch {
    /* ignore */
  }
}

export function readBsiTransferPrefill(): AdvanceTransferHint | null {
  try {
    const raw = sessionStorage.getItem(BSI_TRANSFER_PREFILL_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AdvanceTransferHint;
  } catch {
    return null;
  }
}

export function clearBsiTransferPrefill(): void {
  try {
    sessionStorage.removeItem(BSI_TRANSFER_PREFILL_KEY);
  } catch {
    /* ignore */
  }
}
